import { useEffect, useContext } from 'react'
import { useNavigate } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import { MdChat } from 'react-icons/md'
import { SocketContext } from '@/lib/providers/socket-provider'
import { RootState, AppDispatch } from '@/store'
import { setUnreadCount } from '@/store/slices/internalChatSlice'
import { getUnreadCount } from '@/lib/api/internal-chat/internal-chat-api'

interface InternalChatBadgeProps {
    /**
     * `button`: botão próprio na navbar (uso histórico).
     * `menu-item`: linha dentro do dropdown da foto do usuário.
     *
     * O chat interno saiu da barra em 30/09/2026 — quase ninguém usa, e ele disputava
     * espaço com o que a operação olha o dia inteiro. Foi MOVIDO, não apagado, e o
     * contador veio junto: sem ele, mensagem no chat interno vira invisível, que é pior
     * do que estava.
     */
    variant?: 'button' | 'menu-item'
}

export default function InternalChatBadge({ variant = 'button' }: InternalChatBadgeProps) {
    const navigate = useNavigate()
    const dispatch = useDispatch<AppDispatch>()
    const { socket, isConnected } = useContext(SocketContext)
    const unreadCount = useSelector(
        (state: RootState) => state.internalChat.unreadCount
    )

    // Load initial unread count
    useEffect(() => {
        loadUnreadCount()
    }, [])

    // Listen for unread count updates
    useEffect(() => {
        if (!socket || !isConnected) return

        const handleUnreadCount = (data: { count: number }) => {
            dispatch(setUnreadCount(data.count))
        }

        socket.on('internal-chat:unread-count', handleUnreadCount)

        return () => {
            socket.off('internal-chat:unread-count', handleUnreadCount)
        }
    }, [socket, isConnected, dispatch])

    const loadUnreadCount = async () => {
        const response = await getUnreadCount()
        if (response.success && response.result) {
            dispatch(setUnreadCount(response.result.count))
        }
    }

    const open = () => {
        // O dropdown do DaisyUI fecha ao perder o foco: sem isto, a lista fica aberta
        // por cima da tela para a qual acabamos de navegar.
        ;(document.activeElement as HTMLElement)?.blur()
        navigate('/core/internal-chat')
    }

    if (variant === 'menu-item') {
        return (
            <button type="button" onClick={open}>
                <MdChat className="text-lg" /> Chat interno
                {unreadCount > 0 && (
                    <span className="badge badge-warning badge-sm ml-auto">
                        {unreadCount > 99 ? '99+' : unreadCount}
                    </span>
                )}
            </button>
        )
    }

    return (
        <button
            type="button"
            className={`btn gap-2 indicator ${unreadCount > 0 ? 'btn-secondary' : 'btn-ghost'}`}
            onClick={open}
        >
            <MdChat className="text-xl" />
            <span className="hidden lg:inline">Chat</span>
            {unreadCount > 0 && (
                <span className="badge badge-warning badge-sm indicator-item">
                    {unreadCount > 99 ? '99+' : unreadCount}
                </span>
            )}
        </button>
    )
}
